import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Circle, Square, Bookmark, Radio, Loader2, WifiOff, Clapperboard, Type, MessageSquare, Camera, Newspaper, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PreviewRibbon } from "@/components/cef/cefShared";

/* In-game CREATOR STUDIO (CEF) — /cef/creator/:serverId?token=<player-session>.
   Recording & streaming controls for content creators: record toggle, clip markers,
   stream toggle and broadcast overlay scenes. Actions queue to the hub; nc_bridge.py
   polls /creator/commands/pending and drives the capture stack on the client.
   No token (browser preview) → interactive demo so creators can preview the HUD. */

const ACCENT = "#ef4444";
const OVERLAYS = [
  { id: "lower_third", label: "Lower third", Icon: Type, desc: "Name / server banner" },
  { id: "chat_box", label: "Chat box", Icon: MessageSquare, desc: "On-screen stream chat" },
  { id: "cam_frame", label: "Cam frame", Icon: Camera, desc: "Branded facecam border" },
  { id: "ticker", label: "News ticker", Icon: Newspaper, desc: "Scrolling city headlines" },
  { id: "minimal_hud", label: "Minimal HUD", Icon: Minimize2, desc: "Hide game UI clutter" },
];
const CMD_STATUS = { pending: "text-amber-400 border-amber-500/40", done: "text-emerald-400 border-emerald-500/40", failed: "text-red-400 border-red-500/40" };

const fmt = (s) => `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function CreatorCefPage() {
  const { serverId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const demo = !token;

  const [recording, setRecording] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [overlays, setOverlays] = useState({ lower_third: true, chat_box: false, cam_frame: false, ticker: false, minimal_hud: false });
  const [clipLabel, setClipLabel] = useState("");
  const [commands, setCommands] = useState([]);
  const [busy, setBusy] = useState("");
  const timerRef = useRef(null);

  useEffect(() => {
    if (recording) {
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } else {
      clearInterval(timerRef.current);
      setElapsed(0);
    }
    return () => clearInterval(timerRef.current);
  }, [recording]);

  const loadStatus = useCallback(() => {
    if (demo) return;
    api.get(`/nc/servers/${serverId}/creator/status`, { params: { token } })
      .then((r) => setCommands(r.data.commands))
      .catch(() => {});
  }, [demo, serverId, token]);
  useEffect(() => {
    loadStatus();
    const t = setInterval(loadStatus, 8000);
    return () => clearInterval(t);
  }, [loadStatus]);

  const send = async (action, extra = {}) => {
    if (demo) {
      toast.success(`Preview: ${action.replace("_", " ")} — in-game the bridge executes this`);
      return true;
    }
    setBusy(action);
    try {
      await api.post(`/nc/servers/${serverId}/creator/action`, { action, ...extra }, { params: { token } });
      loadStatus();
      return true;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      return false;
    } finally { setBusy(""); }
  };

  const toggleRecord = async () => {
    const ok = await send(recording ? "record_stop" : "record_start");
    if (ok) setRecording(!recording);
  };

  const toggleStream = async () => {
    const ok = await send(streaming ? "stream_stop" : "stream_start");
    if (ok) setStreaming(!streaming);
  };

  const markClip = async () => {
    const ok = await send("clip_mark", { label: clipLabel });
    if (ok) { setClipLabel(""); toast.success("Clip marked at " + fmt(elapsed)); }
  };

  const toggleOverlay = async (id) => {
    const next = !overlays[id];
    const ok = await send("overlay", { overlay: id, enabled: next });
    if (ok) setOverlays((o) => ({ ...o, [id]: next }));
  };

  return (
    <div data-testid="creator-cef" className="min-h-screen bg-[#050505] text-zinc-100 p-5 sm:p-8 flex flex-col gap-6 relative">
      {demo && <PreviewRibbon what="creator studio" />}

      <div className="flex items-center gap-3 flex-wrap pt-6">
        <Clapperboard className="w-5 h-5" style={{ color: ACCENT }} />
        <h1 className="font-sans font-bold uppercase tracking-widest text-lg">Creator Studio</h1>
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">content capture deck</span>
        <div className="ml-auto flex items-center gap-3">
          {streaming && <span data-testid="live-badge" className="font-mono text-[9px] uppercase tracking-[0.2em] text-red-400 border border-red-500/50 px-2.5 py-1.5 flex items-center gap-1.5 animate-pulse"><Radio className="w-3 h-3" /> Live</span>}
          {recording && <span data-testid="rec-timer" className="font-mono text-sm text-red-400 flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />REC {fmt(elapsed)}</span>}
        </div>
      </div>

      {/* capture controls */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="border border-white/10 bg-black/40 p-6 flex flex-col items-center gap-4">
          <p className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase self-start">Recording</p>
          <button data-testid="record-toggle" onClick={toggleRecord} disabled={busy === "record_start" || busy === "record_stop"}
            className={`w-28 h-28 rounded-full border-4 flex items-center justify-center transition-all ${recording ? "border-red-500 bg-red-500/20 shadow-[0_0_40px_rgba(239,68,68,0.35)]" : "border-white/20 hover:border-red-400 bg-black/60"}`}>
            {busy ? <Loader2 className="w-8 h-8 animate-spin text-zinc-400" /> : recording ? <Square className="w-9 h-9 text-red-400 fill-red-400" /> : <Circle className="w-9 h-9 text-red-500 fill-red-500" />}
          </button>
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{recording ? "Recording — tap to stop" : "Tap to start recording"}</p>
          <div className="flex gap-2 w-full">
            <input data-testid="clip-label" value={clipLabel} maxLength={120} onChange={(e) => setClipLabel(e.target.value)} placeholder="Clip label (optional)"
              className="flex-1 bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-red-500" />
            <button data-testid="clip-mark" onClick={markClip} disabled={busy === "clip_mark"}
              className="border border-amber-500/50 text-amber-400 hover:bg-amber-500/10 font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-2 transition-colors flex items-center gap-1.5 disabled:opacity-40">
              <Bookmark className="w-3.5 h-3.5" /> Mark clip
            </button>
          </div>
        </div>

        {/* overlays */}
        <div className="border border-white/10 bg-black/40 p-6 flex flex-col gap-3">
          <p className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Broadcast overlays</p>
          {OVERLAYS.map(({ id, label, Icon, desc }) => (
            <button key={id} data-testid={`overlay-${id}`} onClick={() => toggleOverlay(id)}
              className={`flex items-center gap-3 border px-4 py-3 text-left transition-colors ${overlays[id] ? "border-red-500/50 bg-red-500/5" : "border-white/10 hover:border-white/30"}`}>
              <Icon className={`w-4 h-4 ${overlays[id] ? "text-red-400" : "text-zinc-500"}`} />
              <span className="flex-1">
                <span className={`font-mono text-[10px] uppercase tracking-[0.15em] block ${overlays[id] ? "text-red-300" : "text-zinc-300"}`}>{label}</span>
                <span className="font-mono text-[8px] text-zinc-600">{desc}</span>
              </span>
              <span className={`font-mono text-[8px] uppercase ${overlays[id] ? "text-red-400" : "text-zinc-600"}`}>{overlays[id] ? "On" : "Off"}</span>
            </button>
          ))}
        </div>

        {/* stream + activity */}
        <div className="flex flex-col gap-5">
          <div className="border border-white/10 bg-black/40 p-6 flex flex-col gap-3">
            <p className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Stream</p>
            <button data-testid="stream-toggle" onClick={toggleStream} disabled={busy === "stream_start" || busy === "stream_stop"}
              className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 border transition-colors flex items-center justify-center gap-2 disabled:opacity-40 ${streaming ? "border-red-500/60 text-red-400 bg-red-500/10 hover:bg-red-500/20" : "border-white/15 text-zinc-200 hover:border-red-400 hover:text-red-300"}`}>
              {busy === "stream_start" || busy === "stream_stop" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />}
              {streaming ? "Stop stream" : "Go live"}
            </button>
            <p className="font-mono text-[8px] text-zinc-600 leading-relaxed">Uses the capture stack configured in your bridge (nc_bridge.py) — the hub relays the command, your machine does the encoding.</p>
          </div>

          <div className="border border-white/10 bg-black/40 p-6 flex flex-col gap-2 flex-1">
            <p className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">Command feed</p>
            {demo && <p className="font-mono text-[9px] text-zinc-600 flex items-center gap-1.5"><WifiOff className="w-3 h-3" /> Preview mode — in-game this lists your queued/acked capture commands.</p>}
            {!demo && commands.length === 0 && <p className="font-mono text-[9px] text-zinc-600">No commands yet — hit record or toggle an overlay.</p>}
            {commands.map((c) => (
              <div key={c.id} data-testid={`creator-cmd-${c.id.slice(0, 8)}`} className="flex items-center gap-2 border-b border-white/5 pb-1.5">
                <span className="font-mono text-[10px] text-zinc-200">{c.action.replace("_", " ")}{c.overlay ? ` · ${c.overlay.replace("_", " ")}` : ""}{c.label ? ` — ${c.label}` : ""}</span>
                <span className={`ml-auto font-mono text-[8px] uppercase border px-1.5 py-0.5 ${CMD_STATUS[c.status]}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
